import { useLocation } from '@Alias/hooks';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getChannel, getEpisode, getLaunchPageType, getShow } from '../../redux/selectors/launch';
import { SubscribeAndAuthViews } from '../../types/analytics';
import { LaunchType } from '../../types/launch';
import { LaunchCode } from '../../utils/launch';
import {
  isActivatePath,
  isAuthenticatePath,
  isAuthorizePath,
  isGoPath,
  isSignupPath,
} from '../../utils/router';
import { useLaunchCode } from '../router/use-launch-code';

export default function useRouteInteractProps() {
  const { pathname } = useLocation();
  const isAuthorize = isAuthorizePath(pathname);
  const isAuthenticate = isAuthenticatePath(pathname);
  const isSignup = isSignupPath(pathname);
  const isDeviceAuth = isActivatePath(pathname);
  const launchCode = useLaunchCode();

  const launchPageType = useSelector(getLaunchPageType);
  const channel = useSelector(getChannel);
  const show = useSelector(getShow);
  const episode = useSelector(getEpisode);

  const resolvedLaunchPageType = isGoPath(pathname) && launchPageType;

  /** KU
   *
   * `launch` state persists between in-app page views. This means a user
   * can visit a launch page, but then have subsequent Analytics.js events fire
   * on non-launch pages. We need to ensure that even when `launch` is
   * specified in Redux state, the current pathname actually corresponds with an
   * asset launch page.
   * */
  const isAssetLaunchPage =
    [LaunchType.CHANNEL, LaunchType.SHOW, LaunchType.EPISODE].includes(
      resolvedLaunchPageType as LaunchType,
    ) && !Object.values(LaunchCode).includes(launchCode as LaunchCode);

  let view: string = resolvedLaunchPageType || SubscribeAndAuthViews.DEFAULT;
  let viewTypeId = '';

  if (launchCode && !isAssetLaunchPage) {
    view = launchCode;
  } else if (resolvedLaunchPageType === LaunchType.CHANNEL) {
    viewTypeId = channel?.id || '';
  } else if (resolvedLaunchPageType === LaunchType.SHOW) {
    viewTypeId = show?.id || '';
  } else if (resolvedLaunchPageType === LaunchType.EPISODE) {
    viewTypeId = episode?.id || '';
  } else if (isAuthenticate) {
    view = SubscribeAndAuthViews.AUTHENTICATE;
  } else if (isAuthorize) {
    view = SubscribeAndAuthViews.AUTHORIZE;
  } else if (isSignup) {
    view = SubscribeAndAuthViews.SIGNUP;
  } else if (isDeviceAuth) {
    view = SubscribeAndAuthViews.ACTIVATE;
  }

  return useMemo(
    () => ({
      view,
      viewTypeId,
    }),
    [view, viewTypeId],
  );
}
